import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {isAuthRedirectError, requireAuth} from "../../shared/services/auth.service";
import {logError, logInfo} from "../../shared/services/logger.service";
import {useToolbar} from "../../components/ToolbarContext";
import {getInvoiceSummaries} from "./invoice.service";
import {InvoiceStatus, InvoiceSummaryDto} from './dtos/invoice.dto';
import {
  CardSpec,
  ChipDef,
  ListRow,
  SecondaryActionButton,
  StatusListPage,
  useStatusList,
} from "../../shared/components/list-page/ListPageKit";
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {addressOneLine, displayPhone, formatMoney, prettyStatusName} from "../../shared/components/list-page/list-format";
import {ListWithRecord, useRecordRoute} from "../../shared/components/record/RecordKit";
import InvoiceRecordPage from "../estimate/record/InvoiceRecordPage";

// Android's invoice chips; a status is its own chip.
const INVOICE_CHIPS: ChipDef[] = [
  {label: 'Created', urlParam: 'created'},
  {label: 'Sent', urlParam: 'sent'},
  {label: 'Overdue', urlParam: 'overdue'},
  {label: 'Paid', urlParam: 'paid'},
];

const chipFor = (status: string | undefined): string | null =>
  INVOICE_CHIPS.some(chip => chip.label === status) ? status! : null;

/*
 * The card's realistic widths, from the Shippy's Tree Service snapshot (866
 * clients, 436 invoices, measured in Chrome with the card's own CSS; 98th
 * percentile, so the longest 2% wrap inside their slot):
 *   name     221px at 18px  "Testing For Status Updates"
 *   phone     99px at 14px  "(208) 631-0182"
 *   address  295px at 14px  "3821 East Thousand Springs Court, Nampa, ID" (the
 *                            client's address, as on Android)
 *   total    145px at 14px  "Invoice total $8,888.88" (the largest real
 *                            invoice is $8,455; budgeted with cents)
 *   status    66px          the "Overdue" pill, the longest status
 * Switch points (cardSwitchPoints): one row from 980px, grouped from 767px.
 */
const CARD_SPEC: CardSpec = {
  title: 221,
  status: 66,
  slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 295},
    {key: 'total', group: 'money', width: 145},
  ],
};

/**
 * The Invoices page, as on Android: status chips + search, one card per
 * invoice titled by its client. An invoice is only born from a completed
 * job, so the title-row button is settings, as on Estimates: Set due dates
 * and follow-ups (SQ-687).
 */
function InvoiceListPage() {
  const navigate = useNavigate();
  const toolbarContext = useToolbar();
  const {hasPermission} = usePermissions();
  // SQ-598: an invoice opens as its own page (/invoices/7) over the list.
  const record = useRecordRoute('/invoices');
  const openRecord = record.open;

  const [invoices, setInvoices] = useState<InvoiceSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    toolbarContext.initToolbar('InvoiceListPage', '');
    logInfo('User navigated to invoice list page');
    void fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchInvoices() {
    setLoading(true);
    setError(null);
    try {
      await requireAuth(navigate);
      // SQ-625: the summaries, not the full invoices: the card needs the
      // client, the status and the total, and the full list carried every
      // job's trees, photos and operations (4.9 MB and 18 s for Shippy's).
      const all = await getInvoiceSummaries(navigate);
      // Deleted invoices are gone for good, as on Android.
      setInvoices(all.filter(invoice => invoice.status !== InvoiceStatus.Deleted));
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error fetching invoices', {}, e as Error);
      setError(`Couldn't load invoices: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  const rows: ListRow[] = useMemo(() => invoices.map(invoice => ({
    key: invoice.id,
    // The client, like the Jobs list; the invoice's number belongs to the
    // record once it is open. Only a card with no client name falls back to it.
    title: invoice.clientName?.trim() || `Invoice #${invoice.companyInvoiceId}`,
    values: {
      phone: displayPhone(invoice.clientPhoneNumber),
      address: addressOneLine(invoice.clientAddress, invoice.clientCity, invoice.clientState),
      total: invoice.totalCost != null
        ? {label: 'Invoice total', value: formatMoney(invoice.totalCost)}
        : {label: 'Invoice total', value: 'Not set', muted: true},
    },
    status: invoice.status ? prettyStatusName(invoice.status) : undefined,
    chip: chipFor(invoice.status),
    // The invoice number is not on the card, but it still finds the invoice.
    searchText: String(invoice.companyInvoiceId ?? ''),
    onClick: () => openRecord(invoice.id),
  })), [invoices, openRecord]);

  const list = useStatusList(rows, INVOICE_CHIPS);

  return (
    <ListWithRecord
      record={typeof record.id === 'number' && (
        <InvoiceRecordPage key={record.id} invoiceId={record.id} onChanged={() => void fetchInvoices()}/>
      )}
      list={(
        <StatusListPage
          title="Invoices"
          // Only for invoices.manage_due_dates, like Estimates' follow-ups button.
          titleButton={hasPermission(PermissionCode.InvoicesManageDueDates)
            ? <SecondaryActionButton label="Set due dates and follow-ups" onClick={() => navigate('/invoices/due-dates')}/>
            : undefined}
          searchLabel="Invoice Filters and Search"
          list={list}
          spec={CARD_SPEC}
          loading={loading}
          error={error}
          onRetry={() => void fetchInvoices()}
        />
      )}
    />
  );
}

export default InvoiceListPage;
